import React from 'react';

// GitHub contribution graph colours
const LEVEL_COLORS = ['#ebedf0', '#9be9a8', '#40c463', '#30a14e', '#216e39'];

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

const CELL_SIZE = 11;
const CELL_GAP = 3;
const COLUMN_WIDTH = CELL_SIZE + CELL_GAP;

const pad = value => String(value).padStart(2, '0');

// Local date key, toISOString would shift the day for anyone east of UTC
const dateKey = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const addDays = (date, amount) => {
  const next = new Date(date.valueOf());
  next.setDate(next.getDate() + amount);
  return next;
};

// Weeks run Sunday to Saturday, like the GitHub graph
const buildWeeks = (from, to) => {
  const firstDay = addDays(from, -from.getDay());
  const weeks = [];

  let cursor = firstDay;
  while (cursor <= to) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(addDays(cursor, i));
    }
    weeks.push(week);
    cursor = addDays(cursor, 7);
  }

  return weeks;
};

const getLevel = (count, max) => {
  if (!count) return 0;
  if (max <= 4) return Math.min(count, 4);

  const share = count / max;
  if (share <= 0.25) return 1;
  if (share <= 0.5) return 2;
  if (share <= 0.75) return 3;
  return 4;
};

function ActivityHeatmap({ days, from, to, label }) {
  const countsByDate = (days || []).reduce(
    (acc, day) => ({ ...acc, [day.date]: day.count }),
    {}
  );
  const maxCount = (days || []).reduce(
    (max, day) => (day.count > max ? day.count : max),
    0
  );

  const weeks = buildWeeks(from, to);
  const today = dateKey(new Date());
  const fromKey = dateKey(from);

  // A month label sits above the first week that starts in a new month
  const monthLabels = weeks.reduce((acc, week, index) => {
    const month = week[0].getMonth();
    const previousMonth = index > 0 ? weeks[index - 1][0].getMonth() : null;
    if (index === 0 || month !== previousMonth) {
      return [...acc, { index, label: MONTH_LABELS[month] }];
    }
    return acc;
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.graph}>
        <div style={styles.dayLabels}>
          <span style={{ ...styles.dayLabel, top: COLUMN_WIDTH }}>Mon</span>
          <span style={{ ...styles.dayLabel, top: COLUMN_WIDTH * 3 }}>Wed</span>
          <span style={{ ...styles.dayLabel, top: COLUMN_WIDTH * 5 }}>Fri</span>
        </div>

        <div>
          <div
            style={{
              ...styles.monthRow,
              width: weeks.length * COLUMN_WIDTH
            }}
          >
            {monthLabels.map(month => (
              <span
                key={`month-${month.index}`}
                style={{
                  ...styles.monthLabel,
                  left: month.index * COLUMN_WIDTH
                }}
              >
                {month.label}
              </span>
            ))}
          </div>

          <div style={styles.weeks}>
            {weeks.map(week => (
              <div key={`week-${dateKey(week[0])}`} style={styles.week}>
                {week.map(day => {
                  const key = dateKey(day);
                  const outOfRange = key < fromKey || key > today;
                  const count = countsByDate[key] || 0;

                  return (
                    <div
                      key={key}
                      title={
                        outOfRange
                          ? undefined
                          : `${count} result${count === 1 ? '' : 's'} on ${key}`
                      }
                      style={{
                        ...styles.cell,
                        backgroundColor: outOfRange
                          ? 'transparent'
                          : LEVEL_COLORS[getLevel(count, maxCount)]
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          ...styles.footer,
          marginLeft: styles.dayLabels.width
        }}
      >
        <span style={styles.footerText}>{label}</span>
        <span style={styles.legend}>
          <span style={styles.footerText}>Less</span>
          {LEVEL_COLORS.map(color => (
            <span
              key={color}
              style={{ ...styles.cell, backgroundColor: color }}
            />
          ))}
          <span style={styles.footerText}>More</span>
        </span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    overflowX: 'auto',
    paddingBottom: '4px'
  },
  graph: {
    display: 'flex'
  },
  dayLabels: {
    position: 'relative',
    width: '34px',
    marginTop: '16px',
    flexShrink: 0
  },
  dayLabel: {
    position: 'absolute',
    right: '6px',
    fontSize: '10px',
    lineHeight: `${CELL_SIZE}px`,
    color: '#57606a'
  },
  monthRow: {
    position: 'relative',
    height: '16px'
  },
  monthLabel: {
    position: 'absolute',
    fontSize: '10px',
    color: '#57606a'
  },
  weeks: {
    display: 'flex',
    gap: `${CELL_GAP}px`
  },
  week: {
    display: 'flex',
    flexDirection: 'column',
    gap: `${CELL_GAP}px`
  },
  cell: {
    width: `${CELL_SIZE}px`,
    height: `${CELL_SIZE}px`,
    borderRadius: '2px',
    flexShrink: 0
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '6px',
    gap: '10px'
  },
  footerText: {
    fontSize: '11px',
    color: '#57606a'
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: `${CELL_GAP}px`
  }
};

export default ActivityHeatmap;
